import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from 'components/ui/Card';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import config from 'utils/config';
import { StyledButton, StyledNavigationContainer, PageContainer } from './HomePage.styles';
import { isDCOffice, isLoungeStaff, isUserCallCenterAgent } from 'utils/user';

import TravelInformationIcon from 'assets/rebrand/pictograms/disruptionDarkTwoTone.svg';
import BookingsIcon from 'assets/rebrand/pictograms/trainDarkTwoTone.svg';
import ShiftsIcon from 'assets/rebrand/pictograms/timetableDarkTwoTone.svg';
import VouchersIcon from 'assets/rebrand/pictograms/upgradeDarkTwoTone.svg';
import ClubEurostarIcon from 'assets/rebrand/pictograms/valueDarkTwoTone.svg';
import CompanyNewsBanner from './CompanyNewsBanner';
import { canUseLowestFareFinder } from 'pages/LowestFareFinder/LowestFareFinder.utils';

const HomePage = ({ user }) => {
  const isGenerateDiscountCodeEnabled = config('ENABLE_GENERATE_DISCOUNT_CODE', { canOverride: true }) === 'true';
  const isLoungeStaffUser = isLoungeStaff(user);
  const isUserWithDCOffice = isDCOffice(user);
  const isCallCenterUser = isUserCallCenterAgent(user);
  const canGenerateDiscountCode = user.canGenerateSilverVoucher;
  useEffect(() => {
    const allowedIssueCreators = config('ALLOWED_ISSUE_CREATORS')?.split(',') || [];
    const userCanCreateIssue = allowedIssueCreators.some((allowedIssueCreator) => user.username === allowedIssueCreator);
    const issueCreatorUrl = config('ISSUE_CREATOR_URL');

    if (userCanCreateIssue && issueCreatorUrl) {
      const script = document.createElement('script');
      script.async = true;
      script.src = issueCreatorUrl;

      document.body.appendChild(script);
    }
  }, [user.username]);

  return (
    <PageContainer>
      <CompanyNewsBanner />
      <StyledNavigationContainer>
        {!isLoungeStaffUser && (
          <Card title={<FormattedMessage id="homepage.bookings" />} image={BookingsIcon}>
            <StyledButton
              spaceWithin
              element={
                <Link
                  to={{
                    pathname: '/search',
                    state: {
                      isPackageBooking: false
                    }
                  }}
                  id="new-booking"
                />
              }
            >
              <FormattedMessage id="homepage.newBooking" />
            </StyledButton>
            {user.canSellPackageBookings && (
              <>
                <StyledButton
                  spaceWithin
                  data-cy="packageBooking"
                  element={
                    <Link
                      to={{
                        pathname: '/search',
                        state: {
                          isPackageBooking: true
                        }
                      }}
                      id="new-package-booking"
                    />
                  }
                >
                  <FormattedMessage id="homepage.packageBooking" />
                </StyledButton>
                <StyledButton
                  spaceWithin
                  data-cy="hotelBooking"
                  element={
                    <Link
                      to={{
                        pathname: '/hotel-only'
                      }}
                      id="new-hotel-booking"
                    />
                  }
                >
                  <FormattedMessage id="homepage.hotelBooking" />
                </StyledButton>
              </>
            )}
            {user.canSellExtrasOnlyBookings && (
              <StyledButton data-cy="extrasOnlyBooking" spaceWithin element={<Link to="/extras-only" />}>
                <FormattedMessage id="homepage.extrasOnlyBooking" />
              </StyledButton>
            )}
            <StyledButton spaceWithin element={<Link to="/search/booking" id="booking-search" />}>
              <FormattedMessage id="homepage.bookingSearch" />
            </StyledButton>
          </Card>
        )}

        <Card title={<FormattedMessage id="homepage.travelInformation" />} image={TravelInformationIcon}>
          <StyledButton
            spaceWithin
            element={
              <Link
                to={{
                  pathname: '/live-trains'
                }}
                id="live-train-information"
              />
            }
          >
            <FormattedMessage id="homepage.liveTrains" />
          </StyledButton>
          <StyledButton
            element={
              <Link
                to={{
                  pathname: '/border-control-requirements'
                }}
                id="border-control-requirements"
              />
            }
          >
            <FormattedMessage id="ees.entryExitCta" />
          </StyledButton>
          {canUseLowestFareFinder(user) && (
            <StyledButton spaceWithin element={<Link to="/lowest-fare-finder" id="lowest-fare-finder" />}>
              <FormattedMessage id="lowestFareFinder.title" />
            </StyledButton>
          )}
        </Card>

        {(user.canManageClubEurostar || isLoungeStaffUser) && (
          <Card title={<FormattedMessage id="homepage.clubEurostar" />} image={ClubEurostarIcon}>
            <StyledButton data-cy="club-eurostar" spaceWithin element={<Link to="/clubeurostar/manage" />}>
              <FormattedMessage id="homepage.clubEurostar" />
            </StyledButton>
          </Card>
        )}

        {(!isLoungeStaffUser || (isUserWithDCOffice && isGenerateDiscountCodeEnabled) || isCallCenterUser) && !isLoungeStaffUser && (
          <Card title={<FormattedMessage id="homepage.administration" />} image={ShiftsIcon} data-cy="administration-card">
            <StyledButton data-cy="shifts" spaceWithin element={<Link to="/shifts" />}>
              <FormattedMessage id="homepage.shifts.subCTA1" />
            </StyledButton>
            {isUserWithDCOffice && isGenerateDiscountCodeEnabled && (
              <StyledButton data-cy="silver-vouchers" spaceWithin element={<Link to="/silver-vouchers-report" />}>
                <FormattedMessage id="homepage.silverVouchers" />
              </StyledButton>
            )}
            {isCallCenterUser && (
              <StyledButton data-cy="be-my-guest" spaceWithin element={<Link to="/be-my-special-guest-report" />}>
                <FormattedMessage id="homepage.beMySpecialGuest" />
              </StyledButton>
            )}
          </Card>
        )}

        {user.canManageVouchers && !isLoungeStaffUser && (
          <Card title={<FormattedMessage id="homepage.vouchers" />} image={VouchersIcon} data-cy="manage-vouchers-card">
            {user.canCreateVouchers && (
              <StyledButton data-cy="create-voucher" spaceWithin element={<Link to="/vouchers/create" />}>
                <FormattedMessage id="homepage.createVoucher" />
              </StyledButton>
            )}
            {user.canRetrieveVouchers && (
              <StyledButton data-cy="manage-voucher" spaceWithin element={<Link to="/vouchers/manage" />}>
                <FormattedMessage id="homepage.manageVoucher" />
              </StyledButton>
            )}
            {canGenerateDiscountCode && isGenerateDiscountCodeEnabled && (
              <StyledButton data-cy="create-discount" spaceWithin element={<Link to="/vouchers/generate-discount-code" />}>
                <FormattedMessage id="generateDiscountCode.generate" />
              </StyledButton>
            )}
            {user.canRetrieveVouchers && (
              <StyledButton data-cy="manage-discount-code" spaceWithin element={<Link to="/discountCodes/manage" />}>
                <FormattedMessage id="homepage.manageDiscountCode" />
              </StyledButton>
            )}
            {user.canManageGenerateBulkVouchers && (
              <StyledButton data-cy="generate-bulk-vouchers" spaceWithin element={<Link to="/generate-bulk-vouchers" />}>
                <FormattedMessage id="homepage.generateBulkVouchers" />
              </StyledButton>
            )}
            {user.canManageProcessBulkVouchers && (
              <StyledButton data-cy="process-bulk-vouchers" spaceWithin element={<Link to="/process-bulk-vouchers" />}>
                <FormattedMessage id="homepage.processBulkVouchers" />
              </StyledButton>
            )}
          </Card>
        )}
      </StyledNavigationContainer>
    </PageContainer>
  );
};

HomePage.propTypes = {
  user: PropTypes.shape({
    canSellExtrasOnlyBookings: PropTypes.bool.isRequired,
    canSellPackageBookings: PropTypes.bool.isRequired,
    canManageVouchers: PropTypes.bool,
    canCreateVouchers: PropTypes.bool,
    canRetrieveVouchers: PropTypes.bool,
    canManageClubEurostar: PropTypes.bool,
    username: PropTypes.string,
    canSellTravelPass: PropTypes.bool,
    canGenerateSilverVoucher: PropTypes.bool,
    canManageGenerateBulkVouchers: PropTypes.bool,
    canManageProcessBulkVouchers: PropTypes.bool
  })
};

export default HomePage;
